/* <bogo-gift-modal> — free-gift chooser (snippets/bogo-gift-modal.liquid, #134).
   Native <dialog>. Opens from the owning section's main ATC form submit (the
   sticky bar requestSubmit()s that same form — one code path) and from any
   [data-bogo-modal-open] page trigger, but ONLY when the count of BOGO-eligible
   units in the cart is EVEN before the click (0, 2, 4…): odd counts fall
   through to a plain add so a lone item just gets its BOGO partner. The count
   is the drawer's server-computed data-bogo-qty (#281, 2026-09-09, Codex F02:
   tagged lines only, gift-with-order lines excluded — whole-cart parity broke
   with an accessory in the cart and would break with a free gift line); the
   tracked unit count below is only the fallback for a page with no drawer.

   #368 (2026-09-16) — THE RECEIPT IS NEVER FALSE, AND NO FAILURE IS A DEAD END.
   Before this, the paid item was only added once the gift had been picked, so
   the green "Added to cart" row showed while the cart was still empty and a
   shopper who left there had bought nothing; a failed add then fell through to
   a full-page POST that landed on a raw /cart/add or "Error 1015" page. The
   order is now:
     1. The PAID item's add starts AT ONCE, drawer closed (one cart call) —
        startPaidAdd() runs before any await. #461 (2026-09-17) moved the
        popup off that same instant: it opens from a 500 ms setTimeout, so the
        chooser reads as a beat after the tap, not a screen slammed up before
        the click registers. this.openPending blocks a submit landing in that
        window from reopening the timer or firing a second add. A failure
        already SETTLED by the time the timer fires opens the popup with that
        failure and its retry (#543 Fix 9, 2026-09-23; before, it skipped the
        popup for nativeFallback(), #145/#368.2).
     2. The confirmation row is revealed only when /cart/add.js has ANSWERED
        for that line; until then the same slot reads "Adding to your cart…".
        Option B's design (#344) and its confirmation wording (#348) are
        unchanged — only WHEN the receipt appears is.
     3. A gift pick adds the gift with ONE more cart call. The gift is always a
        plain one-time line, never a selling plan, whatever the paid line
        carries (Eli's #390 rule): same variant + a plain paid line merges into
        one line of two exactly as the old two-item payload did, while a
        subscription or a line with properties keeps its own line (#161 F1).
     4. A settled failure is answered INSIDE the popup — the message plus a
        retry that re-runs exactly the add that failed. Everything that is not
        a settled error closes onto the DRAWER with the true cart: this design
        has no cart page (Eli 09-16), and Shopify's /cart stays as the
        no-script fallback only. #543 Fix 9 (2026-09-23): a refusal never
        leaves the page — the full-page POST is kept for a real bot challenge
        only (cart.js marks it); a 429, 422, 5xx or lost connection is
        answered in the popup, or in the drawer when the popup is gone.
     5. The control the shopper actually pressed is recorded BEFORE the popup
        opens and handed to the drawer, so closing the drawer returns focus to
        it (#429).

   EVERY no-gift exit — backdrop click, X, Escape, "I'll choose something
   else" — is now only a close onto the drawer: the sale was already made when
   the popup opened (#134 notes 5/7 — the shopper pressed Add To Cart, so
   dismissing the upsell never cancels the buy). After a settled failure an
   exit only closes (#543 Fix 9): nothing was bought, so there is no drawer to
   show, and the popup never reopens.

   FAILURE-PROOFING (#145, from Eli trapped in the modal by a bot-challenge 429
   on 2026-08-13; recovery rule rewritten for #261, 2026-09-09, Codex F01, and
   re-pointed at the drawer for #368): an exit gesture must NEVER depend on a
   cart call succeeding — the exits here touch the network not at all. Every
   add goes through runAdd(), which settles one AJAX attempt into done/failed/
   pending and never re-sends; each branch is documented on that method and on
   exit(). The worst case stays a visibly missing add, never a duplicate.

   Modules arrive via data-*-module VERSIONED asset urls: a bare relative
   import would be a second evaluated instance whose unguarded
   customElements.define throws (verified on preview). Loaded eagerly — a lazy
   module would race the first click. */

class BogoGiftModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.form = document.getElementById(this.dataset.formId);
    /* data-initial-cart-count, NOT data-cart-count — the badge updater in
       cart.js rewrites every [data-cart-count] element's textContent on each
       cart:updated, which would wipe this element's children including the
       dialog (burned #134: showModal then threw "not in a Document" and the
       uncaught error killed every later ATC submit). */
    this.count = parseInt(this.dataset.initialCartCount, 10) || 0;
    this.paidState = 'idle';
    this.paidRun = null;

    /* kicked off at connect so a click never waits on module load */
    this.cartReady = import(this.dataset.cartModule);
    this.watchCount();

    /* #429: the opener has to be known BEFORE the popup opens, and
       event.submitter is null for the sticky bar's requestSubmit() — so the
       last control the shopper actually pressed is tracked here, in capture,
       for both pointer and keyboard activation. #543 Fix 7: the same listener
       times every finger/pointer press for tooSoon(). */
    for (const type of ['pointerdown', 'keydown']) {
      document.addEventListener(
        type,
        (event) => {
          if (type === 'pointerdown') this.lastPointerDownAt = performance.now();
          const el = event.target && event.target.closest && event.target.closest('button, [role="button"], input[type="submit"], a[href]');
          if (el) this.pressed = { el, at: Date.now() };
        },
        true
      );
    }

    /* Capture at document beats cart.js's <product-form> submit listener
       (bound on the form itself) regardless of module load order. */
    document.addEventListener(
      'submit',
      (event) => {
        if (event.target !== this.form) return;
        if (this.eligibleCount() % 2 !== 0) return; /* odd cart: plain add, no modal */
        event.preventDefault();
        event.stopPropagation();
        this.open(event.submitter);
      },
      true
    );

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-bogo-modal-open]');
      if (!trigger) return;
      /* first instance owns page-level triggers */
      if (document.querySelector('bogo-gift-modal') !== this) return;
      event.preventDefault();
      if (this.eligibleCount() % 2 !== 0) {
        this.plainAdd(trigger); /* odd cart: add the paid item, skip the chooser */
        return;
      }
      this.open(trigger);
    });

    /* Backdrop clicks arrive with target === dialog, but so do clicks on the
       dialog's own padding — and an exit is a real gesture, so only
       coordinates outside the dialog box may count as "outside". */
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      const inside =
        event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside && !this.tooSoon(event)) this.exit();
    });
    /* Escape fires 'cancel'. exit() never awaits the network, so it can always
       run — the old "let the native close through mid-attempt" branch existed
       only because dismissing used to buy. */
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.exit();
    });
    for (const el of this.querySelectorAll('[data-modal-close], [data-modal-decline]')) {
      el.addEventListener('click', (event) => {
        if (!this.tooSoon(event)) this.exit();
      });
    }

    for (const option of this.querySelectorAll('[data-gift-option]')) {
      option.addEventListener('click', (event) => {
        if (!this.tooSoon(event)) this.choose(option);
      });
    }

    /* #368: the retry re-runs exactly the add that failed (set by feedback()). */
    this.querySelector('[data-modal-retry]')?.addEventListener('click', () => {
      const again = this.retryAction;
      this.feedback('');
      if (again) again();
    });
  }

  /* Live cart count via the bus (critical.js is importmap-pinned like cart.js).
     Mutation ordering guard mirrors the badge's: never let an older mutation's
     count overwrite a newer one. On any failure the load-time seed stands. */
  async watchCount() {
    try {
      const { bus } = await import(this.dataset.criticalModule);
      let lastMutation = 0;
      bus.on('cart:updated', (event) => {
        const mutation = event.detail?.mutation || 0;
        const count = event.detail?.data?.item_count;
        /* NO optimistic math from add responses: /cart/add.js item quantities
           are NOT reliably "amount added" once lines merge (double-counted in
           #134 verification). Server truth only — item_count when the payload
           has it.
           #368.3 (2026-09-16): the /cart.js background refresh that used to sit
           here is GONE. It was a second, identical read of the endpoint
           cart.js's badge listener already re-reads on the same event — one of
           the four cart calls per add the 09-14 audit counted. The only
           decision this number feeds is the even/odd gate, and since #281 that
           reads the drawer's server-computed [data-bogo-qty] first; the seed
           below matters only on a page with no drawer, where it stays correct
           at its load-time value. */
        if (typeof count !== 'number') return;
        if (mutation && mutation < lastMutation) return;
        if (mutation) lastMutation = mutation;
        this.count = count;
      });
    } catch {
      /* bus unavailable: the server-seeded count still gates correctly on first use */
    }
  }

  /* #281 (2026-09-09): the one definition of "eligible units" is the drawer's data-bogo-qty,
     rewritten by Liquid on every cart render; read at decision time, never cached. Falls back to
     the tracked whole-cart count when no drawer is on the page. */
  eligibleCount() {
    const inner = document.querySelector('cart-drawer [data-drawer-inner][data-bogo-qty]');
    const n = inner ? parseInt(inner.dataset.bogoQty, 10) : NaN;
    return Number.isFinite(n) ? n : this.count;
  }

  /* #429: the element the shopper pressed — explicit submitter first, then the tracked press
     (fresh only), then whatever holds focus. Never the popup's own furniture, never <body>: the
     drawer is handed this element so ITS close lands on the Add to cart button instead of
     dropping focus to the top of a 17,000 px page. */
  resolveOpener(explicit) {
    const recent = this.pressed && Date.now() - this.pressed.at < 2000 ? this.pressed.el : null;
    for (const el of [explicit, recent, document.activeElement]) {
      if (!el || el === document.body || !el.isConnected) continue;
      if (typeof el.focus !== 'function' || this.contains(el)) continue;
      return el;
    }
    return null;
  }

  /* Scroll lock is CSS-only ([html:has(&[open])]:overflow-hidden on the
     dialog), so no close listener to desync. */
  open(opener) {
    if (this.dialog.open || this.openPending) return;
    this.opener = this.resolveOpener(opener);
    this.feedback('');
    /* A previous opening that already settled starts over; one still in flight is REUSED by
       startPaidAdd() below rather than sent twice (#261: a duplicate paid unit is the one
       outcome worse than a missing one). */
    if (!this.paidRun) this.paidState = 'idle';
    /* #461 (2026-09-17): startPaidAdd() FIRST, immediately. this.openPending guards the 500 ms
       window below — a second submit inside it can't restart the timer or fire a second add —
       and clears the instant the window resolves either way. */
    this.openPending = true;
    /* #543 Fix 7 (2026-09-23, audit perf-interaction-3): the pressed button shows it was pressed AT ONCE —
       before, nothing changed for the 0.5 s until the popup, which is what invited the second tap. The
       theme's existing busy look (.btn[aria-busy='true']: dimmed, not pressable), no new text; cleared the
       moment the timer resolves, whichever way it goes. */
    const busy = this.opener;
    busy?.setAttribute('aria-busy', 'true');
    this.startPaidAdd();
    this.receipt('pending');
    this.syncPaidVariant();
    setTimeout(() => {
      this.openPending = false;
      busy?.removeAttribute('aria-busy');
      /* #543 Fix 9 (2026-09-23): already FAILED by now → the popup opens with the failure and its retry,
         and the shopper stays on the product page. Only a real bot challenge skips the popup for
         nativeFallback(); startPaidAdd()'s own failure branch already made that same idempotency-guarded
         call the moment the add settled, so this one is a no-op (the form is submitted once at most). */
      if (this.paidState === 'failed') {
        if (this.challenged()) {
          this.nativeFallback();
          return;
        }
        this.dialog.showModal();
        this.openedAt = performance.now();
        this.feedback(this.errorText(), () => this.startPaidAdd());
        return;
      }
      this.dialog.showModal();
      this.openedAt = performance.now();
    }, 500);
  }

  /* #543 Fix 7 (2026-09-23, audit code-review-1): the popup opens 500 ms after the Add to cart tap, right
     where a quick second tap on the same spot lands — on a gift card (a gift the shopper never chose: Neck &
     Chest in the recordings) or on the dim area / "I'll choose something else" (the popup closes, no gift).
     A pointer tap inside the popup counts only when its finger went down at least 450 ms after the popup
     opened; deliberate picks take far longer. Keyboard and assistive-technology clicks (detail 0) always
     count, and so does any click with no pointer press seen on this page. Escape and Try again are not
     gated. The audit's check of "finger down before the popup opened" missed the 0.6-0.9 s repeat taps it
     was written for; this rule stopped them in the verify run and let a pick 1.8 s in through. */
  tooSoon(event) {
    if (!event || event.detail === 0 || this.openedAt === undefined || this.lastPointerDownAt === undefined) return false;
    return this.lastPointerDownAt < this.openedAt + 450;
  }

  /* Option B (Eli 2026-09-14): the confirmation row and the gift that IS this product both follow the
     variant actually being added — pick the charcoal wand and the thumbnail at the top and the free wand
     below are charcoal too. #368.4 (2026-09-16) extends that to the gift's ALT TEXT, its name and its
     struck PRICE (the sheet mask showed the $9 single's price beside the $39 5-pack). The variant map is
     printed by snippets/bogo-gift-modal.liquid. */
  syncPaidVariant() {
    if (this.variantMap === undefined) {
      const json = this.querySelector('[data-bogo-variants]');
      try {
        this.variantMap = json ? JSON.parse(json.textContent) : null;
      } catch {
        this.variantMap = null;
      }
    }
    const v = this.variantMap && this.variantMap[this.paidVariantId()];
    if (!v) return;
    const swap = (img, alt) => {
      if (!img || !v.img) return;
      img.removeAttribute('srcset');
      img.src = v.img;
      if (typeof alt === 'string') img.alt = alt;
    };
    const rename = (el, separator) => {
      if (!el || !v.name || v.name === 'Default Title') return;
      if (!el.dataset.base) el.dataset.base = el.textContent.trim();
      el.textContent = el.dataset.base + separator + v.name;
    };
    /* the receipt thumbnail sits beside the name it describes, so it stays decorative (alt="") */
    swap(this.querySelector('[data-bogo-receipt-img]'));
    rename(this.querySelector('[data-bogo-receipt-name]'), ' · ');
    const handle = this.dataset.productHandle;
    const self = handle && this.querySelector('[data-gift-option][data-gift-handle="' + handle + '"]');
    if (!self) return;
    self.dataset.variantId = String(this.paidVariantId());
    swap(self.querySelector('img'), v.alt);
    const title = self.querySelector('[data-gift-title]');
    rename(title, ' · ');
    if (title) title.setAttribute('title', title.textContent.trim());
    const strike = self.querySelector('[data-gift-price] del');
    if (strike && v.strike) strike.textContent = v.strike;
  }

  paidVariantId() {
    const input = this.form?.querySelector('input[name="id"]');
    return parseInt(input?.value, 10) || parseInt(this.dataset.paidVariantId, 10);
  }

  /* #161 F1 (2026-09-07): the paid line must carry everything the form would post — the
     variant, the Skio selling plan and any line-item properties — exactly as cart.js does,
     otherwise a "Subscribe & Save" shopper who goes through the chooser buys one-time. */
  paidItem(quantity = 1) {
    const item = { id: this.paidVariantId(), quantity };
    if (!this.form) return item;
    const data = new FormData(this.form);
    const plan = parseInt(data.get('selling_plan'), 10);
    if (plan) item.selling_plan = plan;
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value) properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  /* #368: the receipt row's two states live in one slot, so revealing the confirmation never
     moves the layout. 'pending' = "Adding to your cart…"; 'done' = option B's green row, shown
     only once the cart has answered for the paid line. #543 Fix 9 review (2026-09-23): 'failed'
     shows neither line — the error below says what happened, so the row no longer claims the add
     is still going; a retry puts it back to 'pending'. */
  receipt(state) {
    const row = this.querySelector('[data-bogo-receipt]');
    if (row) row.dataset.state = state;
    this.querySelector('[data-bogo-receipt-pending]')?.toggleAttribute('hidden', state !== 'pending');
    this.querySelector('[data-bogo-receipt-done]')?.toggleAttribute('hidden', state !== 'done');
  }

  /* #368: one place writes the popup's answer — the message and, when the caller offers one, the
     retry control. replaceChildren() with no argument leaves the paragraph truly :empty, so its
     empty:hidden rule still applies (replaceChildren('') would leave an empty text node). */
  feedback(message, again = null) {
    const line = this.querySelector('[data-modal-error]');
    if (line && message) line.replaceChildren(message);
    else if (line) line.replaceChildren();
    this.retryAction = again;
    const retry = this.querySelector('[data-modal-retry]');
    if (!retry) return;
    retry.toggleAttribute('hidden', !again);
    /* a control that appears without focus is easy to miss; focus stays inside the open dialog */
    if (again && this.dialog.open) retry.focus();
  }

  /* The server's own description when it gave one (cart.js attaches it), else the plain line the
     locale file provides through data-error-fallback — "Cart request failed (429)" is not a
     sentence for a shopper. */
  errorText() {
    return (this.lastError && this.lastError.cartDescription) || this.dataset.errorFallback || (this.lastError && this.lastError.message) || '';
  }

  /* #543 Fix 9 (2026-09-23): the last failure was a real bot challenge (cart.js post() marks it). Only that
     one still takes the page-level POST; a 429, 422, 5xx or a lost connection never leaves the page. */
  challenged() {
    return !!(this.lastError && this.lastError.cartChallenge);
  }

  /* #543 Fix 9: a refused add the shopper is no longer in the popup to see (they closed it while the add
     was in flight, or the add came from a page trigger with no popup) is answered in the drawer's alert
     region with the same sentence the popup would print. The drawer shows the cart as it is: the refused
     add changed nothing, so no refresh (and no second cart call) is spent. */
  async failedToDrawer() {
    await this.toDrawer();
    document.querySelector('cart-drawer [data-cart-error]')?.replaceChildren(this.errorText());
  }

  addItems(items, options = {}) {
    return this.cartReady.then(({ CartAPI }) => CartAPI.add(items, options));
  }

  withTimeout(promise, ms) {
    return Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error('cart timeout')), ms))]);
  }

  /* #254 (2026-09-07), widened for #261 (2026-09-09, Codex F01): the cart's line items from
     /cart.js, or null when the read fails (bot challenge, offline) — parsed defensively, never
     thrown. Read before an add and again when the add timed out or failed, so the outcome can be
     told per LINE. (The #254 version compared item_count, so any growth — another tab, an app
     adding a line — passed as "our add landed".) #368.3: the deadline is a parameter, and the
     pre-add read uses a short one — the popup is already on screen, and a stalled endpoint must
     not delay the paid add behind it. */
  async cartLines(ms = 3000) {
    try {
      const res = await this.withTimeout(fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }), ms);
      const data = await res.json();
      return Array.isArray(data?.items) ? data.items : null;
    } catch {
      return null;
    }
  }

  /* #261 (2026-09-09, Codex F01): one key per cart line — variant + selling plan + properties, the
     exact signature the item was posted with (CartAPI.add posts items verbatim), so a sent item is
     matched to ITS line only. Accepts both shapes: an item as we post it
     ({ id, selling_plan, properties }) and a /cart.js line
     ({ variant_id, selling_plan_allocation: { selling_plan: { id } }, properties }). */
  lineKey(line) {
    const variant = Number(line?.variant_id ?? line?.id) || 0;
    const plan = Number(line?.selling_plan ?? line?.selling_plan_allocation?.selling_plan?.id) || 0;
    const props = line?.properties && typeof line.properties === 'object' ? line.properties : {};
    const pairs = Object.keys(props)
      .filter((key) => props[key] !== null && props[key] !== undefined && props[key] !== '')
      .sort()
      .map((key) => [key, String(props[key])]);
    return `${variant}|${plan}|${JSON.stringify(pairs)}`;
  }

  /* quantity per line key; lines sharing a key are summed. Shopify treats a missing quantity as 1. */
  lineQuantities(lines) {
    const map = new Map();
    for (const line of lines || []) {
      const key = this.lineKey(line);
      map.set(key, (map.get(key) || 0) + (Number(line?.quantity) || 1));
    }
    return map;
  }

  /* #261 (2026-09-09, Codex F01): did OUR lines land? For every item we sent, its line's quantity now
     must be at least what it was before plus what we sent. "Before" is 0 for a line that did not
     exist — and 0 for every line when the pre-add read failed, so then "present at the sent
     quantity" is the best evidence available. A failed re-read (null) is never "landed". */
  linesLanded(before, after, items) {
    if (!after) return false;
    const was = this.lineQuantities(before || []);
    const now = this.lineQuantities(after);
    const sent = this.lineQuantities(items);
    for (const [key, qty] of sent) {
      if ((now.get(key) || 0) < (was.get(key) || 0) + qty) return false;
    }
    return sent.size > 0;
  }

  /* #368.3: ONE cart call per add, and one settled answer for it. Returns
       'done'    — /cart/add.js answered for these items, or its response was lost but a per-line
                   re-read shows them in the cart;
       'failed'  — a settled server error or rejection AND the re-read does not show them (a failed
                   re-read lands here too: the whole cart endpoint is unhealthy, the #145 case);
       'pending' — no settled answer after the 8 s cap plus a 3 s grace. NOTHING is re-sent: the
                   first request may still commit and a second one is how a shopper pays twice.
     The pre-add read carries its own short deadline so a stalled /cart.js cannot hold the add
     behind it; without a "before", "present at the sent quantity" is the evidence used. A caller
     that already knows the cart's state passes it as options.before and spends no read at all. */
  async runAdd(items, options = {}) {
    const { before: known, ...addOptions } = options;
    const before = known === undefined ? await this.cartLines(1500) : known;
    this.lastError = null;
    const attempt = this.addItems(items, addOptions);
    attempt.catch(() => {}); /* the outcome is read below; no unhandled rejection */
    let settled = 'landed';
    try {
      await this.withTimeout(attempt, 8000);
    } catch (error) {
      if (!(error && error.message === 'cart timeout')) this.lastError = error;
      settled = await Promise.race([
        attempt.then(
          () => 'landed',
          (error2) => {
            this.lastError = error2;
            return 'failed';
          }
        ),
        new Promise((resolve) => setTimeout(() => resolve('pending'), 3000)),
      ]);
    }
    if (settled === 'landed') return 'done';
    const after = await this.cartLines();
    if (this.linesLanded(before, after, items)) return 'done';
    return settled === 'failed' ? 'failed' : 'pending';
  }

  /* #368.1: the paid item goes in FIRST, with the drawer closed, and the confirmation row is
     revealed only from the real answer. One run per opening; a run still in flight from an earlier
     opening is reused, never duplicated (it carries that opening's variant — the receipt can only
     lag a variant change by the width of that window, which beats a second charge). */
  startPaidAdd() {
    if (this.paidRun) return this.paidRun;
    this.receipt('pending');
    /* #543 Fix 9 (audit code-review-3): a retry after a failure is in flight again, so it must not still
       read 'failed' — exit() takes the failure branch from this value alone. */
    this.paidState = 'pending';
    this.paidItems = [this.paidItem()];
    const items = this.paidItems;
    this.paidRun = (async () => {
      this.paidBefore = await this.cartLines(1500);
      const outcome = await this.runAdd(items, { openDrawer: false, before: this.paidBefore });
      this.paidState = outcome;
      if (outcome === 'done') {
        this.receipt('done');
      } else if (outcome === 'failed') {
        /* Popup still up → the failure is answered there, with the retry. #543 Fix 9 (2026-09-23): popup not
           up yet (open()'s 500 ms timer is still running) → that timer opens it with this failure. Popup
           already gone (the shopper exited while this add was in flight) → the drawer's alert line. Only a
           real bot challenge takes the documented #145 native POST, the one thing that lets a challenge
           resolve at page level; a plain refusal sent that way landed the shopper on a raw "rate limited"
           or /cart page. Nothing here waited for the exit; the exit did not wait for this (#261). */
        this.receipt('failed');
        if (this.dialog.open) this.feedback(this.errorText(), () => this.startPaidAdd());
        else if (this.challenged()) this.nativeFallback();
        else if (!this.openPending) this.failedToDrawer();
      } else {
        /* pending: never re-sent — close onto the drawer showing the true cart */
        this.forceClose();
        this.toDrawer({ refresh: true });
      }
      return outcome;
    })();
    this.paidRun.catch(() => {}).finally(() => {
      this.paidRun = null;
    });
    return this.paidRun;
  }

  ensurePaid() {
    if (this.paidState === 'done') return Promise.resolve('done');
    return this.paidRun || this.startPaidAdd();
  }

  /* #368.2: close onto the drawer showing the TRUE cart — never a /cart navigation (Eli 09-16: the
     design has no cart page; Shopify's /cart is the no-script fallback only). The drawer re-renders
     itself from every settled cart call, so after a 'done' add it is already right; only an
     unsettled add needs the extra no-op refresh, and if even that fails the drawer prints its own
     "your cart was updated" notice rather than showing a cart we cannot vouch for (#267). Awaiting
     cart.js also guarantees <cart-drawer> is upgraded, so open() exists. #543 Fix 9 review
     (code-review-7, 2026-09-23): refresh is asked for only after an add that never answered, and that
     add can still land after the refresh — so the notice now prints either way. Before, the shopper
     watched "Adding to your cart…" for 11 s and then got a drawer with no word of what happened. */
  async toDrawer({ refresh = false } = {}) {
    try {
      const { CartAPI } = await this.cartReady;
      const drawer = document.querySelector('cart-drawer');
      /* already open (an earlier exit put it there) → leave it alone: open() re-focuses the panel and would
         pull focus off whatever the shopper is on inside the drawer */
      if (drawer && !drawer.hasAttribute('open')) drawer.open?.(this.opener);
      if (!refresh) return;
      try {
        await this.withTimeout(CartAPI.update({}), 4000);
      } catch {
        /* the refresh failed too: the notice below covers it */
      }
      drawer?.noticeStale?.();
    } catch {
      /* modules unavailable: the dialog is closed either way — never strand the shopper */
    }
  }

  /* Close unconditionally — the absolute exit. Nothing here may throw out. */
  forceClose() {
    try {
      this.dialog.close();
    } catch {
      /* never let a close error strand the shopper */
    }
  }

  /* Degraded-but-never-stuck path: classic form POST — submit() fires no 'submit' event, so
     nothing intercepts, and any bot-challenge interstitial resolves itself. #543 Fix 9 (2026-09-23):
     reached ONLY for a real bot challenge (challenged()) — from an exit taken after the paid add
     SETTLED IN FAILURE (#368.2), from startPaidAdd()'s own failure branch when the dialog isn't open,
     from plainAdd(), and — #461 (2026-09-17) — from open()'s 500 ms timer when that same pre-open
     failure is still current. The timer and startPaidAdd() can fire for the SAME failure, so
     this.fallbackSent makes every call after the first a no-op: submit() fires at most once, the
     "never a duplicate" rule #261 already wrote for the cart calls above. */
  nativeFallback() {
    if (this.fallbackSent) return;
    this.fallbackSent = true;
    this.forceClose();
    try {
      this.form?.submit();
    } catch {
      /* modal is already closed; the page stays usable either way */
    }
  }

  /* A gift pick: the paid line has to be in the cart first (#368.1), then ONE more cart call for
     the gift. The gift is ALWAYS a plain one-time line — no selling plan, no properties, whatever
     the paid line carries (Eli's #390 rule, 09-16). Same variant + a plain paid line: Shopify
     merges the two adds into one line of two, exactly as the old single two-item payload did; a
     subscription or a line with properties keeps its own line (#161 F1). */
  async choose(option) {
    if (this.busy) return;
    this.busy = true;
    option.setAttribute('aria-busy', 'true');
    try {
      const paid = await this.ensurePaid();
      /* failed → the popup is showing the message and the retry; pending → already on the drawer */
      if (paid !== 'done') return;
      const giftId = parseInt(option.dataset.variantId, 10);
      if (!giftId) return;
      /* the gift add spends NO second pre-add read: the cart as it stands is the paid add's own "before" plus
         the paid items it committed (the 09-14 audit counted four cart calls per add; this is one of them). A
         paid read that failed leaves this null — exactly the case linesLanded() already documents. */
      const before = this.paidBefore ? this.paidBefore.concat(this.paidItems || []) : null;
      const outcome = await this.runAdd([{ id: giftId, quantity: 1 }], { openDrawer: false, before });
      if (outcome === 'failed') {
        /* the PAID line is already bought, so a failed gift needs no native POST — the popup offers the retry,
           and a shopper who has already exited is re-offered the free item by the drawer's own BOGO engine */
        if (this.dialog.open) this.feedback(this.errorText(), () => this.choose(option));
        return;
      }
      this.forceClose();
      this.toDrawer({ refresh: outcome !== 'done' });
    } finally {
      option.removeAttribute('aria-busy');
      this.busy = false;
    }
  }

  /* Odd eligible count from a page trigger: no chooser, just the paid item and the drawer. Same
     settled-answer policy as every other add on this element. */
  async plainAdd(opener) {
    if (this.busy) return;
    this.busy = true;
    this.opener = this.resolveOpener(opener);
    try {
      const outcome = await this.runAdd([this.paidItem()], { opener: this.opener });
      /* #543 Fix 9: the page-level POST only for a real bot challenge; any other refusal stays on the page */
      if (outcome === 'failed') this.challenged() ? this.nativeFallback() : this.failedToDrawer();
      else if (outcome === 'pending') this.toDrawer({ refresh: true });
    } finally {
      this.busy = false;
    }
  }

  /* #145: an exit gesture NEVER waits on a cart call (the every-exit-buys design plus the store's
     bot-protection 429s once left Eli imprisoned in this dialog — X, Escape and the backdrop all
     re-attempted the doomed add). The buy already went out when the popup opened, so an exit is a
     close plus one decision: a paid add that SETTLED IN FAILURE with nothing in the cart only closes
     (#543 Fix 9, 2026-09-23: it never reopens and never leaves the page — the shopper can tap Add to
     cart again), except for a real bot challenge, which takes the documented native form POST that
     lets the challenge resolve at page level; anything else goes to the drawer with the true cart. */
  exit() {
    this.forceClose();
    if (this.paidState === 'failed') {
      if (this.challenged()) this.nativeFallback();
      return;
    }
    /* An add still in flight re-renders the drawer itself when it answers, so the common "open, then X" exit
       spends no extra cart call; only an add that went unanswered needs the no-op refresh. */
    /* #543 Fix 9 review (code-review-6, 2026-09-23): until that add answers, the drawer shows the cart as it was
       before it ("Your cart is empty" on a first add). It now wears the theme's existing in-flight look
       (cart-drawer[aria-busy]: dimmed, not pressable) until the add settles, for 2 s at most: on a phone the
       drawer's own close button sits inside the dimmed region, and an exit never waits on a cart call (#145). */
    const drawer = this.paidRun && document.querySelector('cart-drawer');
    if (drawer) {
      let held = true;
      const release = () => {
        if (held) drawer.removeAttribute('aria-busy');
        held = false;
      };
      drawer.setAttribute('aria-busy', 'true');
      const cap = setTimeout(release, 2000);
      this.paidRun.catch(() => {}).finally(() => {
        clearTimeout(cap);
        release();
      });
    }
    this.toDrawer({ refresh: !this.paidRun && this.paidState !== 'done' });
  }
}

if (!customElements.get('bogo-gift-modal')) customElements.define('bogo-gift-modal', BogoGiftModal);
